import { useContext, useCallback, useRef } from 'react';
import { AdsContext } from '@buzzfeed/adlib/dist/module/bindings/react/contexts';
import ABeagleContext from '../../contexts/abeagle';
import BuzzContext from '../../contexts/buzz';
import { isWordChain, isHuedoku, isSoupOfTheDay, isGeotemp } from '../../utils/gameInterstitial';

export const GAME_INTERSTITIAL_PREFETCH_EVENT = 'game-interstitial-prefetch';

/**
 * Config registry for game interstitial ads in bf_bpage_ui.
 *
 * To add a new game:
 * 1. Add a config entry here.
 * 2. Add the experiment to constants/ab-tests.js and Ads.jsx experiments array.
 *
 * Config fields:
 * - id: unique identifier
 * - experiment: ABeagle experiment name
 * - match(buzz): returns true if this buzz contains this game type
 * - prefetchOnMount: if true, mount <AdUnit> immediately when eligible; if false, wait
 *   for onBroadcast to return 'prefetch'
 * - onBroadcast({ message, messageData, variant }): called for every T3 broadcast.
 *   Return 'prefetch' to mount the ad slot, 'trigger' to show the ad, or null to ignore.
 */
const GAME_INTERSTITIAL_CONFIGS = [
  {
    id: 'word-chain',
    experiment: 'word-chain-interstitial',
    match: isWordChain,
    prefetchOnMount: false,
    onBroadcast: ({ message, messageData, variant }) => {
      const transitionType =
        messageData?.placeholder?.questionId ||
        messageData?.placeholders?.[0]?.questionId ||
        messageData?.transitionType;

      const variantMatches = () => {
        if (variant === 'level_3_ad') return transitionType === 'hard';
        if (variant === 'results_ad') return transitionType === 'results';
        return false;
      };

      if (message === 'bfp-word-chain-interstitial-prefetch') {
        return variantMatches() ? 'prefetch' : null;
      }
      if (message === 'bfp-word-chain-ad-placeholders-ready') {
        return variantMatches() ? 'trigger' : null;
      }
      return null;
    },
  },
  {
    id: 'huedoku',
    experiment: 'huedoku-interstitial',
    match: isHuedoku,
    prefetchOnMount: true,
    onBroadcast: ({ message, variant }) => {
      if (message === 'bfp-game-huedoku-level-1-complete' && variant === 'level_1_ad') {
        return 'trigger';
      }
      return null;
    },
  },
  {
    id: 'soup-of-the-day',
    experiment: 'soup-of-the-day-interstitial',
    match: isSoupOfTheDay,
    prefetchOnMount: true,
    onBroadcast: ({ message, variant }) => {
      if (message === 'bfp-game-soup-of-the-day-give-up-btn-click' && variant === 'give_up') {
        return 'trigger';
      }
      return null;
    }
  },
  {
    id: 'geo_temp',
    experiment: 'geotemp-interstitial',
    match: isGeotemp,
    prefetchOnMount: (variant) => variant === 'level_2_ad',
    onBroadcast: ({ message, variant }) => {
      if (message === 'bfp-game-geotemp-continue-to-round-2' && variant === 'level_2_ad') {
        return 'trigger';
      } else if (message === 'bfp-game-geotemp-continue-to-round-2' && variant === 'level_3_ad') {
        return 'prefetch';
      } else if (message === 'bfp-game-geotemp-continue-to-round-3' && variant === 'level_3_ad') {
        return 'trigger';
      } else if (message === 'bfp-game-geotemp-continue-to-round-3' && variant === 'see_your_score') {
        return 'prefetch';
      } else if (message === 'bfp-game-geotemp-see-your-score' && variant === 'see_your_score') {
        return 'trigger';
      }
      return null;
    },
  },
];

/**
 * Unified hook for game interstitial ads (word chain, huedoku, future games).
 * Returns:
 * - enabled: whether the interstitial ad is enabled for the current game and variant.
 * - prefetchOnMount: whether the ad slot should be mounted immediately when eligible.
 * - handleBroadcast(message, messageData): call from T3 message handler.
 *   Returns 'trigger' if the ad should be shown, 'prefetch' if the slot should mount, or null.
 */
export function useGameInterstitialAd() {
  const { buzz = {} } = useContext(BuzzContext);
  const { experiments, getExperimentValue } = useContext(ABeagleContext);
  const adsContext = useContext(AdsContext);

  const config = GAME_INTERSTITIAL_CONFIGS.find((c) => c.match(buzz)) || null;

  const variant = (config && experiments?.loaded)
    ? (getExperimentValue(config.experiment, { rejectErrors: false }) || 'control')
    : 'control';
  const variantRef = useRef(variant);
  variantRef.current = variant;

  const configRef = useRef(config);
  configRef.current = config;

  const enabled = !!config && variant !== 'control';
  const prefetchOnMount = typeof config?.prefetchOnMount === 'function'
    ? config.prefetchOnMount(variant)
    : !!config?.prefetchOnMount;

  const handleBroadcast = useCallback((message, messageData) => {
    const currentConfig = configRef.current;
    if (!currentConfig) return null;

    const action = currentConfig.onBroadcast({
      message,
      messageData,
      variant: variantRef.current,
    });

    if (action === 'prefetch' && adsContext?.eventBus) {
      adsContext.eventBus.trigger(GAME_INTERSTITIAL_PREFETCH_EVENT);
    }
    return action;
  }, [adsContext?.eventBus]);

  return { enabled, prefetchOnMount, handleBroadcast };
}

export { GAME_INTERSTITIAL_CONFIGS };
export default useGameInterstitialAd;
